import { FC, ReactElement, useState } from 'react';
import * as Yup from 'yup';
import i18next from 'i18next';
import { EntityScope, PromMetric } from '../../../../shared/utils/enums';
import { Alert, Button, Form, Spinner, Stack } from 'react-bootstrap';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { HttpMethod, useAuthFetchFromRestAPI } from '../../util/api';
import { Typeahead } from 'react-bootstrap-typeahead';
import { EnduranceTrainingTypeDto } from '../../../../shared/dtos/enduranceTrainingType.dto';
import { UnitDto } from '../../../../shared/dtos/unit.dto';
import { EnduranceTrainingTypeRequest } from '../../../../shared/requests/enduranceTrainingType.request';

type FormValues = {
  deName: string;
  enName: string;
  trainingMinutes: number;
  isBillable: boolean;
  unitId: string;
  excludedPromMetrics: PromMetric[];
};

const PROM_METRICS: PromMetric[] = [PromMetric.STEPS, PromMetric.DISTANCE, PromMetric.HEART_RATE];

const isPromMetric = (option: unknown): option is PromMetric => {
  return PROM_METRICS.some((metric: PromMetric) => metric === option);
};

type EnduranceTrainingTypeFormProps = {
  initialData?: EnduranceTrainingTypeDto;
  entityScope: EntityScope;
  units: UnitDto[];
  onClose: () => void;
  onSubmit: (data: EnduranceTrainingTypeDto) => void;
};

const EnduranceTrainingTypeForm: FC<EnduranceTrainingTypeFormProps> = ({
  initialData,
  entityScope,
  units,
  onSubmit,
  onClose
}: EnduranceTrainingTypeFormProps): ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { authFetch } = useAuthFetchFromRestAPI();

  const initialValues: FormValues = {
    deName: initialData?.deName ?? '',
    enName: initialData?.enName ?? '',
    trainingMinutes: initialData?.trainingMinutes ?? 0,
    isBillable: initialData?.isBillable ?? false,
    unitId: initialData?.unit?.id ?? '',
    excludedPromMetrics: initialData?.excludedPromMetrics ?? [],
  };

  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    deName: Yup.string().required(i18next.t('generic.required')),
    enName: Yup.string().required(i18next.t('generic.required')),
    trainingMinutes: Yup.number().required(i18next.t('generic.required')),
    isBillable: Yup.boolean().default(false),
    unitId: Yup.string().required(i18next.t('generic.required')),
    excludedPromMetrics: Yup.array()
      .of(Yup.mixed<PromMetric>().oneOf(PROM_METRICS).required())
      .required()
      .default([]),
  });

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);

    try {
      const payload = createInput(values);
      const result = await submitData(payload);
      onSubmit(result);

    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const createInput = (values: FormValues): EnduranceTrainingTypeRequest => {
    const unit = units.find((unit) => unit.id === values.unitId);

    if (!unit) {
      throw new Error('Unit not found');
    }

    return {
      deName: values.deName.trim(),
      enName: values.enName.trim(),
      trainingMinutes: values.trainingMinutes,
      isBillable: values.isBillable,
      unitId: unit.id,
      excludedPromMetrics: values.excludedPromMetrics
    };
  };

  const submitData = async (payload: EnduranceTrainingTypeRequest): Promise<EnduranceTrainingTypeDto> => {
    const scope: string = entityScope === EntityScope.CORE ? 'core' : 'tenant';

    const url: string = initialData
      ? `/api/v1/endurance-training-type/${scope}/${encodeURIComponent(initialData.id)}`
      : `/api/v1/endurance-training-type/${scope}`;

    const method: HttpMethod = initialData ? 'PATCH' : 'POST';

    return authFetch(url, { method: method, body: payload });
  };


  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, touched, errors, handleSubmit, handleChange, handleBlur, isSubmitting, setFieldValue }: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1 }}>
            <Stack gap={3}>
              <Form.Group controlId="deName">
                <Form.Label>
                  {i18next.t('enduranceTrainingType.deName')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="deName"
                  value={values.deName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.deName && !!errors.deName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.deName}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="enName">
                <Form.Label>
                  {i18next.t('enduranceTrainingType.enName')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="enName"
                  value={values.enName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.enName && !!errors.enName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.enName}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="trainingMinutes">
                <Form.Label>
                  {i18next.t('enduranceTrainingType.trainingMinutes')}
                </Form.Label>
                <Form.Control
                  type="number"
                  name="trainingMinutes"
                  value={values.trainingMinutes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.trainingMinutes && !!errors.trainingMinutes}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.trainingMinutes}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="isBillable">
                <Form.Check
                  type="checkbox"
                  name="isBillable"
                  label={i18next.t('enduranceTrainingType.isBillable')}
                  checked={values.isBillable}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group controlId="unitId">
                <Form.Label>{i18next.t('enduranceTrainingType.unit')}</Form.Label>
                <Typeahead
                  id="unit-typeahead"
                  labelKey={(option: any) =>
                    `${i18next.language === 'de' ? option.deName : option.enName} (${i18next.language === 'de' ? option.deUnit : option.enUnit})`}
                  options={units}
                  selected={values.unitId
                    ? units.filter((unit) => unit.id === values.unitId)
                    : []
                  }
                  onChange={(selected: any[]) => setFieldValue('unitId', selected.length > 0 ? selected[0].id : '')}
                  clearButton
                  disabled={units.length === 0}>
                </Typeahead>
                {touched.unitId && errors.unitId && (
                  <div className="invalid-feedback d-block">
                    {errors.unitId}
                  </div>
                )}
                {units.length === 0 && (
                  <div className="form-text">
                    {i18next.t('enduranceTrainingTypeModal.noUnits')}
                  </div>
                )}

              </Form.Group>

              <Form.Group controlId="excludedPromMetrics">
                <Form.Label>{i18next.t('enduranceTrainingType.excludedPromMetrics')}</Form.Label>
                <Typeahead
                  id="excluded-prom-metrics-typeahead"
                  multiple
                  labelKey={(option) => i18next.t(`enduranceTrainingType.promMetric.${option}`)}
                  options={PROM_METRICS}
                  selected={values.excludedPromMetrics}
                  onChange={(selected) => setFieldValue('excludedPromMetrics', selected.filter(isPromMetric))}
                  clearButton>
                </Typeahead>
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting || isSaving}>
                  {i18next.t('generic.close')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || isSaving || units.length === 0}>
                  {i18next.t(initialData ? 'generic.save' : 'generic.create')}
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EnduranceTrainingTypeForm;
